import type { SectionsConfig } from '@backoffice-frontend/authenticated-app-view'
import { Portals } from '@backoffice-frontend/common'
import {
  currentEnvironmentNotBuildEnvironment,
  getEnvironment,
  isLocalhost,
} from '@backoffice-frontend/environment'
import {
  backroundEnv,
  getEnvLabel,
  useMediaQuery,
  useNavigationDrawerState,
  useSetNavigationDrawerState,
  useTheme,
} from '@backoffice-frontend/patterns'
import type { ReactNode } from 'react'
import { MobileFilters } from './Mobile/MobileFilters'
import { MobileHeader } from './Mobile/MobileHeader'
import { NavigationDrawerContent } from './NavigationDrawerContent'
import { NavigationDrawerFooter } from './NavigationDrawerFooter'
import { NavigationDrawerLogo } from './NavigationDrawerLogo'

type NavigationDrawerProps = {
  navigationSections: SectionsConfig
  logo: ReactNode
  logoVariant?: 'dark' | 'light'
}

export const NavigationDrawer = ({
  navigationSections,
  logo,
  logoVariant,
}: NavigationDrawerProps) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const open = useNavigationDrawerState()
  const setOpen = useSetNavigationDrawerState()

  const handleClose = () => {
    setOpen(false)
  }

  return (
    //TODO: Remove this line
    // biome-ignore lint/complexity/noUselessFragments: for review redability
    <>
      <div
        css={{
          background: theme.semantic.ColorSurfaceDefault,
          boxShadow: theme.shadows[2],
          [theme.breakpoints.up('md')]: {
            height: '100%',
            width: '80px',
            display: 'flex',
            flexDirection: 'column',
            transition: 'width 200ms',
            ...(open && {
              width: '280px',
            }),
          },
          [theme.breakpoints.down('md')]: {
            height: '100%',
            display: 'none',
            zIndex: 251,
            width: '0',
            flexDirection: 'column',
            transition: 'width 200ms',
            ...(open && {
              width: '100%',
              display: 'flex',
            }),
          },
        }}
      >
        {(isLocalhost || currentEnvironmentNotBuildEnvironment()) && (
          <span
            css={theme => ({
              height: theme.space.Space6,
              padding: `${theme.space.Space1} 0`,
              background: `linear-gradient(90deg, ${backroundEnv[getEnvironment()].dark} 0%, ${backroundEnv[getEnvironment()].bright} 100%)`,
              color:
                theme.semantic
                  .ColorContentOnContrastCriticalSuccessDefaultAlternative,
              fontWeight: 'bold',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              letterSpacing: '0.75px',
            })}
          >
            {open
              ? getEnvironment().toUpperCase()
              : getEnvLabel(getEnvironment())}
          </span>
        )}
        {isMobile ? (
          <>
            <MobileHeader
              onClose={handleClose}
              logo={logo}
              logoVariant={logoVariant}
            />
            <div id={Portals.DedicatedDropdown} />
            <NavigationDrawerContent
              filters={<MobileFilters />}
              open={open}
              navigationSections={navigationSections}
              onAreaChange={handleClose}
            />
          </>
        ) : (
          <>
            <NavigationDrawerLogo
              open={open}
              logo={logo}
              logoVariant={logoVariant}
            />
            <NavigationDrawerContent
              open={open}
              navigationSections={navigationSections}
            />
            <NavigationDrawerFooter
              onClick={() => setOpen(!open)}
              open={open}
            />
          </>
        )}
      </div>
    </>
  )
}
